import {w3cwebsocket as W3CWebSocket} from 'websocket';

import log from '../logger/log';

const RESUB_OFFSET = 2 * 60 * 1000;

const instances = {};

export default class DamWebSocketClient {
    constructor(assetId, isCreateNew, options, serialNumber, config = {}) {
        const {logPrefix = 'damWebSocketClient', KeepAliveManagerClass} = config;

        const instance = instances[assetId];

        if (isCreateNew && instance) {
            instance.disconnectClient();
        }

        if (isCreateNew) {
            instances[assetId] = this;
            this.assetId = assetId;
            this.serialNumber = serialNumber;
            this.logPrefix = logPrefix;
            this.KeepAliveManagerClass = KeepAliveManagerClass;
            this.initClient(options);
        }

        return instances[assetId];
    }

    getExpiry(options) {
        throw new Error(`${this.logPrefix}: getExpiry() is not implemented`);
    }

    initClient(options) {
        this.isConnected = false;

        if (options?.url) {
            const url = new URL(options.url);
            const loggerUrl = `${url.origin}${url.pathname}`;

            this.exp = this.getExpiry(options);

            log.info(
                `${this.logPrefix}: start connection for assetId: ${this.assetId} with url: ${loggerUrl}`,
                this.serialNumber
            );

            this.client = new W3CWebSocket(options.url);
            this.keepAliveManager = new this.KeepAliveManagerClass({
                client: this.client,
                assetId: this.assetId,
                onError: this.disconnectWithReconnectClient.bind(this),
            });
            this.scheduleResubscribe();
        } else {
            throw new Error(`${this.logPrefix}: Client can't be initialized for assetId: ${this.assetId}.`);
        }
    }

    scheduleResubscribe() {
        clearTimeout(this.resubscribeTimeout);

        const delay = Math.max(this.exp - Date.now() - RESUB_OFFSET, 0);

        this.resubscribeTimeout = setTimeout(() => {
            this.disconnectWithReconnectClient();
        }, delay);
    }

    startKeepAliveInterval() {
        this.keepAliveManager.start();
    }

    stopKeepAliveInterval() {
        this.keepAliveManager.stop();
    }

    isConnectedCheck(assetId) {
        if (this.assetId !== assetId) {
            return false;
        }
        return this.isConnected;
    }

    disconnectClient() {
        if (this.client && this.isConnected) {
            this.client.close(1000);
            this.stopClient();
        }
        instances[this.assetId] = null;
    }

    disconnectWithReconnectClient() {
        if (this.client && this.isConnected) {
            // 4000 - 4999 reserved for private use
            this.client.close(4000);
            this.stopClient();
        }
        instances[this.assetId] = null;
    }

    attachMessageHandler(onNewMessageHandler) {
        if (!this.client) return;

        this.client.onmessage = (message) => {
            log.info(
                `${this.logPrefix}: new message for assetId: ${this.assetId} message: ${message?.data}`,
                this.serialNumber
            );
            onNewMessageHandler(message);

            this.stopKeepAliveInterval();
            this.startKeepAliveInterval();
        };
    }

    attachConnectHandler(onConnectHandler) {
        if (!this.client) return;

        this.client.onopen = () => {
            this.isConnected = true;
            log.info(`${this.logPrefix}: connected for assetId: ${this.assetId}`, this.serialNumber);
            onConnectHandler();

            this.startKeepAliveInterval();
        };
    }

    attachCloseHandler(onCloseHandler) {
        if (!this.client) return;

        this.client.onclose = (message) => {
            this.isConnected = false;
            log.info(
                `${this.logPrefix}: offline for assetId: ${this.assetId}. Message code: ${message.code}`,
                this.serialNumber
            );
            onCloseHandler(message);

            this.stopClient();
        };
    }

    attachErrorHandler(onErrorHandler) {
        if (!this.client) return;

        this.client.onerror = (error) => {
            this.isConnected = false;
            log.warn(
                `${this.logPrefix}: Error connection for ${this.assetId}. Message code: ${error.code}`,
                error,
                this.serialNumber
            );
            onErrorHandler(error);

            this.stopClient();
        };
    }

    stopClient() {
        if (!this.client) return;

        clearTimeout(this.resubscribeTimeout);
        this.stopKeepAliveInterval();
    }
}
